/**
 * Loading placeholders shaped like the content they stand in for.
 *
 * A shimmering outline of the cards that are about to appear tells a host the
 * page is working and where things will land, which a bare "Loading…" line does
 * not; it also stops the layout jumping when the data arrives. Decorative only:
 * the wrapper carries the accessible "loading" state.
 */

export function SkeletonCards({ count = 3, label = "Loading" }: { count?: number; label?: string }) {
  return (
    <div
      className="grid"
      style={{ gridTemplateColumns: "repeat(auto-fill, minmax(18rem, 1fr))" }}
      role="status"
      aria-label={label}
    >
      {Array.from({ length: count }, (_, index) => (
        <div key={index} className="card skeleton-card" aria-hidden="true">
          <div className="row" style={{ gap: "0.8rem", flexWrap: "nowrap" }}>
            <span className="skeleton skeleton-avatar" />
            <div className="stack" style={{ gap: "0.5rem", flex: 1 }}>
              <span className="skeleton skeleton-title" />
              <span className="skeleton skeleton-line" style={{ width: "45%" }} />
            </div>
          </div>
          <span className="skeleton skeleton-line" style={{ width: "70%" }} />
          <div className="row">
            <span className="skeleton skeleton-button" />
            <span className="skeleton skeleton-button" />
          </div>
        </div>
      ))}
    </div>
  );
}

export function SkeletonLines({ lines = 3, label = "Loading" }: { lines?: number; label?: string }) {
  return (
    <div className="stack" style={{ gap: "0.6rem" }} role="status" aria-label={label}>
      {Array.from({ length: lines }, (_, index) => (
        <span
          key={index}
          className="skeleton skeleton-line"
          aria-hidden="true"
          style={{ width: index === lines - 1 ? "55%" : "100%" }}
        />
      ))}
    </div>
  );
}

/** Whole-page wait: the brand mark, gently floating. */
export function PageLoader({ label = "Loading" }: { label?: string }) {
  return (
    <div className="page-loader" role="status" aria-label={label}>
      <span className="brand-mark" aria-hidden="true" />
    </div>
  );
}
